import { useEffect, useState } from "react"
import { getMinutesSecondsText, getNextAppointments } from "@helpers"
import { useScreenSize } from "@hooks"
import dayjs from "dayjs";
import { ArrowRightCircleIcon } from "@heroicons/react/24/outline";
import PropTypes from "prop-types";

const screenSizeMap = {
  "xs": 1,
  "sm": 1,
  "md": 2,
  "lg": 2,
  "xl": 2,
  "2xl": 3,
  "3xl": 3,
  "4xl": 4
};

const AppointmentCountdownRibbon = ({ classNames = "", handleClick, schedule = [] }) => {
  const screenSize = useScreenSize()
  const [countdowns, setCountdowns] = useState([]);

  const getDurationToAppointment = (appointment) => {
    if (!appointment) return null

    const secondsToAppointmentStarts = appointment.starts_at.diff(dayjs(), "seconds");
    const secondsToAppointmentEnds = appointment.ends_at.diff(dayjs(), "seconds")

    if (secondsToAppointmentStarts <= 0 && secondsToAppointmentEnds > 0) {
      return "Now"
    }
    
    if (secondsToAppointmentEnds < 0) {
      return "Expired"
    }

    // Meeting starts in less than 5 minutes
    if (secondsToAppointmentStarts < 300) {
      const minutes = Math.floor(secondsToAppointmentStarts / 60)
      const seconds = secondsToAppointmentStarts % 60
      const duration = getMinutesSecondsText(minutes, seconds)

      return duration
    } 
    
    // Meeting starts in more than 5 minutes
    return dayjs.duration(secondsToAppointmentStarts, "seconds").humanize()  
  }

  const getAppointmentsForScreenSize = (appointments) => {
    const maxVisibleAppointments = screenSizeMap[screenSize] || 1
    const currentFutureAppointments = appointments.filter(appointment => appointment.type !== "expired")
    const pastAppointments = appointments.filter(appointment => appointment.type === "expired")
    const noOfAppointmentsToPrepend = maxVisibleAppointments - currentFutureAppointments.length
    const appointmentsToPrepend =  noOfAppointmentsToPrepend > 0 ? pastAppointments.slice(-noOfAppointmentsToPrepend) : []

    return [...appointmentsToPrepend, ...currentFutureAppointments].slice(0, maxVisibleAppointments)
  }

  const getClasses = (appointment) => {
    const classes = []
    if (appointment.type === "now") classes.push("bg-green-500 hover:bg-green-600")
    if (appointment.type === "expired") classes.push("bg-content2 text-white")
    if (appointment.type === "future") classes.push("bg-blue-500 hover:bg-blue-600")

    return classes.join(" ")
  }

  const getCountdowns = () => {
    const appointments = getNextAppointments(schedule, 4, true) || []

    return appointments.map(appointment => {
      const durationToAppointment = getDurationToAppointment(appointment)

      return {
        id: appointment.id,
        parent: appointment.parent,
        duration: durationToAppointment,
        starts_at: appointment.starts_at,
        type: durationToAppointment === "Now" ? "now" : durationToAppointment === "Expired" ? "expired" : "future",
      }
    })
  }

  useEffect(() => {
    const interval = setInterval(() => {
      setCountdowns(getCountdowns());
    }, 1000); // Runs every second

    return () => clearInterval(interval); // Cleanup on unmount
  }, [schedule]); // eslint-disable-line react-hooks/exhaustive-deps
    
  const appointments = getAppointmentsForScreenSize(countdowns)

  return (
    <div className={`flex justify-between gap-2 ${classNames?.container || ""}`}>
      {appointments.map((appointment, index) => {
        const conditionalClasses = getClasses(appointment)

        return (
          <div 
            className={`w-full hover:cursor-pointer flex px-6 py-1 text-center text-white justify-center rounded-full whitespace-nowrap ${conditionalClasses}`} 
            key={index}
            onClick={() => handleClick(appointment.id)}
          >
            <span>
              {appointment.starts_at.format("H:mm")} - <strong>{appointment.parent}</strong> - {appointment.duration}
            </span>

            <ArrowRightCircleIcon className="w-6 h-6 ml-2" />
          </div>
        )
    })}
    </div>
  )
}

AppointmentCountdownRibbon.propTypes = {
  classNames: PropTypes.object,
  handleClick: PropTypes.func,
  schedule: PropTypes.arrayOf(
    PropTypes.shape({
      starts_at: PropTypes.object.isRequired,
      ends_at: PropTypes.object.isRequired
    })
  ),    
}

export default AppointmentCountdownRibbon